import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchFirstRunGuideStatusService,
  postFirstRunGuideEventService,
} from '../../Services/firstRunGuide.service';

// The guide's four steps, in order. The server reports which are done and which
// one is current (see backend first_run_guide_service.py).
export const GUIDE_STEPS = [
  'create_job',
  'screen_resume',
  'schedule_interview',
  'track_interview',
];

// Every response carries the event counter as it stood when the request was
// sent. A response sent before the latest event is stale (it may still say
// "create_job" after the job was saved) and is dropped, so the guide never
// jumps back to a step the user has already finished.
const withSeq = (request) => async (arg, { getState }) => {
  const seq = getState().firstRunGuide.eventSeq;
  return { status: await request(arg), seq };
};

export const fetchFirstRunGuide = createAsyncThunk(
  'firstRunGuide/fetch',
  withSeq(() => fetchFirstRunGuideStatusService()),
);

// event: "job_created" | "completed" | "dismissed" | "restart"
export const sendFirstRunGuideEvent = createAsyncThunk(
  'firstRunGuide/event',
  withSeq((event) => postFirstRunGuideEventService(event)),
);

// Called by the job builders after a successful save, so the guide moves on to
// screening the moment the job exists.
export const guideJobCreated = createAsyncThunk(
  'firstRunGuide/jobCreated',
  withSeq(() => postFirstRunGuideEventService('job_created')),
  {
    condition: (_, { getState }) => {
      const { visible, step } = getState().firstRunGuide;
      return visible && step === 'create_job';
    },
  },
);

const initialState = {
  visible: false,
  step: null, // one of GUIDE_STEPS
  steps: {}, // { create_job: true, screen_resume: false, ... }
  // Skipped earlier with steps left: the Start Guide button on the Help page brings it back
  restartable: false,
  // Bumped by the Setup guide button; FirstRunGuide reopens the pop-up on change
  openRequest: 0,
  // Bumped when an event is sent; see withSeq
  eventSeq: 0,
};

const applyStatus = (state, { payload }) => {
  if (payload.seq !== state.eventSeq) return;
  const status = payload.status || {};
  state.visible = !!status.visible;
  state.step = status.step ?? null;
  state.steps = status.steps || {};
  state.restartable = !!status.restartable;
};

export const firstRunGuideSlice = createSlice({
  name: 'firstRunGuide',
  initialState,
  reducers: {
    requestGuideOpen: (state) => {
      state.openRequest += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFirstRunGuide.fulfilled, applyStatus)
      // Events update the screen at once rather than after the round trip
      .addCase(sendFirstRunGuideEvent.pending, (state, { meta }) => {
        state.eventSeq += 1;
        if (meta.arg === 'dismissed') state.visible = false;
      })
      .addCase(sendFirstRunGuideEvent.fulfilled, applyStatus)
      .addCase(guideJobCreated.pending, (state) => {
        state.eventSeq += 1;
        state.step = 'screen_resume';
        state.steps = { ...state.steps, create_job: true };
      })
      .addCase(guideJobCreated.fulfilled, applyStatus);
  },
});

export const { requestGuideOpen } = firstRunGuideSlice.actions;
